import { CheckIcon } from '@radix-ui/react-icons'
import { InfoIcon } from 'lucide-react'
import React from 'react'

import { listToProseString } from 'components/members/utils'
import { membersMarketplace } from 'rails_routes'
import { TextLink } from 'slab'
import { cn } from 'utils'
import {
  type MemberOnboardingEnrollmentEvent,
  type MemberOnboardingMemberPlanChangesProps,
  type Money,
} from 'utils/web_api.schemas'

import { getAddedParticipantNames, getRemovedParticipantNames, isCurrentPlanOffThatch, isEnrollmentEventQle, isEnrollmentEventRenewal, isNextPlanEmpty } from '../../utils'
import BudgetComparisonSection from '../BudgetComparisonSection/BudgetComparisonSection'
import MarketplaceProductSection from '../MarketplaceProductSection/MarketplaceProductSection'

interface BudgetAndPlanChangesSectionProps {
  condensed: boolean;
  planChanges: MemberOnboardingMemberPlanChangesProps;
  qmeEnabled: boolean;
  hasTieredAllowances: boolean;
  companyName: string;
  enrollmentEvent?: MemberOnboardingEnrollmentEvent | null;
}

interface BaseSectionProps {
  condensed: boolean;
  planChanges: MemberOnboardingMemberPlanChangesProps;
  maxAmountSubunits: number;
  enrollmentEvent?: MemberOnboardingEnrollmentEvent | null;
}

type MonthlyAllowanceSectionProps = BaseSectionProps & {
  companyName: string;
}
type MonthlyPremiumSectionProps = BaseSectionProps
type RemainderOrYouPaySectionProps = BaseSectionProps & {
  qmeEnabled: boolean;
  directionDifferent: boolean;
  remainingBalance: Money;
  remainingBalanceIsPositive: boolean;
}

export const MonthlyAllowanceSection = ({
  condensed,
  planChanges,
  maxAmountSubunits,
  companyName,
  enrollmentEvent,
}: MonthlyAllowanceSectionProps) => {
  const allowanceChange = planChanges.year_over_year_change.allowance_amount
  let description = ''

  if (isEnrollmentEventRenewal(enrollmentEvent)) {
    // For renewal events, use the original company-focused messages
    if (allowanceChange.subunits > 0) {
      description =
        (companyName || 'Your company') +
        ' is increasing your allowance to help offset the rising cost of insurance and ensure you continue to get great coverage.'
    } else if (allowanceChange.subunits < 0) {
      description =
        'Your allowance will be changing next year. If you want to find coverage that better fits your budget, you can shop for new plans.'
    } else {
      description =
        (companyName || 'Your company') +
        " is offering you the same allowance as last year to make sure you can keep your coverage if it's working for you."
    }
  } else {
    // For QLE events, use participant-focused messages
    const addedNames = getAddedParticipantNames(
      planChanges.current_plan_year.covered_participants || [],
      planChanges.next_plan_year.covered_participants || []
    )
    const removedNames = getRemovedParticipantNames(
      planChanges.current_plan_year.covered_participants || [],
      planChanges.next_plan_year.covered_participants || []
    )

    if (allowanceChange.subunits > 0) {
      if (addedNames.length > 0) {
        description = 'Your allowance will go up to help with the costs of the dependents you added'
      } else {
        // Should not hit this case only dependent changes will increase the allowance in QLEs
        description = 'Your allowance will go up'
      }
    } else if (allowanceChange.subunits < 0) {
      if (removedNames.length > 0) {
        const namesList = listToProseString(removedNames)
        description = `Your allowance will decrease since ${namesList} won't be on your insurance anymore`
      } else {
        description = 'Your allowance will decrease'
      }
    } else {
      description = "Your allowance won't change from this QLE"
    }
  }

  const isQle = isEnrollmentEventQle(enrollmentEvent)
  const beforeLabel = isQle ? 'Before' : planChanges.current_plan_year.plan_year.toString()
  const afterLabel = isQle ? 'After' : planChanges.next_plan_year.plan_year.toString()

  return (
    <BudgetComparisonSection
      title='Monthly allowance'
      description={description}
      condensed={condensed}
      beforeLabel={beforeLabel}
      afterLabel={afterLabel}
      beforeAmount={planChanges.current_plan_year.allowance_amount}
      afterAmount={planChanges.next_plan_year.allowance_amount}
      change={planChanges.year_over_year_change.allowance_amount}
      xAxisMaxAmountSubunits={maxAmountSubunits}
      increaseIsGreen
      useMinLabelWidth={isQle}
    />
  )
}

export const MonthlyPremiumSection = ({
  condensed,
  planChanges,
  maxAmountSubunits,
  enrollmentEvent,
}: MonthlyPremiumSectionProps) => {
  const allowanceAmount = planChanges.next_plan_year.allowance_amount
  const premiumAmount = planChanges.next_plan_year.premium_total

  const premiumChange = planChanges.year_over_year_change.premium_total
  let description = ''

  const offThatchOrMedicare = isCurrentPlanOffThatch(planChanges) && isNextPlanEmpty(planChanges)

  if (isEnrollmentEventRenewal(enrollmentEvent)) {
    // Handle the case where the plan is off thatch/medicare and there is no plan for the next year
    if (offThatchOrMedicare) {
      description =
      'Insurance rates generally increase every year due to factors like rising medical costs, prescription drug prices, ' +
      'economic inflation, and increased healthcare utilization. Here\'s how much your plans increased compared to last year:'
    }

    // Now handle the case where the plan is not off thatch/medicare and there is a plan for the next year
    if (premiumChange.subunits > 0) {
      description =
      'Insurance rates generally increase every year due to factors like rising medical costs, ' +
      'prescription drug prices, economic inflation, and increased healthcare utilization.'
    } else if (premiumChange.subunits < 0) {
      description =
      'Insurance prices can sometimes go down in the individual market due to increased competition ' +
      'or if the insurance company has moved you to a slightly different plan. Lucky you!'
    } else {
      description =
      'Insurance prices can sometimes stay the same in the individual market due to ' +
      'increased competition and wider availability of plans. Lucky you!'
    }
  } else {
    // For QLE events, use participant-focused messages with names
    const addedNames = getAddedParticipantNames(
      planChanges.current_plan_year.covered_participants || [],
      planChanges.next_plan_year.covered_participants || []
    )
    const removedNames = getRemovedParticipantNames(
      planChanges.current_plan_year.covered_participants || [],
      planChanges.next_plan_year.covered_participants || []
    )

    // Determine if there are multiple plans (dental and/or vision in addition to medical)
    const hasMultiplePlans = !!(
      (planChanges.current_plan_year.dental_plan || planChanges.next_plan_year.dental_plan) ||
      (planChanges.current_plan_year.vision_plan || planChanges.next_plan_year.vision_plan)
    )
    const planText = hasMultiplePlans ? 'plans' : 'plan'

    if (premiumChange.subunits > 0) {
      if (addedNames.length > 0) {
        const namesList = listToProseString(addedNames)
        description = `Your current ${planText} will be more expensive after ${namesList} ${addedNames.length === 1 ? 'is' : 'are'} added`
      } else {
        description = `Your current ${planText} will be more expensive`
      }
    } else if (premiumChange.subunits < 0) {
      if (removedNames.length > 0) {
        const namesList = listToProseString(removedNames)
        description = `Your current ${planText} will be cheaper if ${namesList} ${removedNames.length === 1 ? 'is' : 'are'} taken off`
      } else {
        description = `Your current ${planText} will be cheaper`
      }
    } else {
      description = `Your current ${planText} will still cost the same amount`
    }
  }

  const showCoveredMessage =
    !offThatchOrMedicare && premiumAmount.subunits <= allowanceAmount.subunits

  const isQle = isEnrollmentEventQle(enrollmentEvent)
  const beforeLabel = isQle ? 'Before' : planChanges.current_plan_year.plan_year.toString()
  const afterLabel = isQle ? 'After' : planChanges.next_plan_year.plan_year.toString()

  return (
    <BudgetComparisonSection
      title='Total monthly insurance cost'
      description={description}
      condensed={condensed}
      beforeLabel={beforeLabel}
      afterLabel={afterLabel}
      beforeAmount={planChanges.current_plan_year.premium_total}
      afterAmount={offThatchOrMedicare ? null : premiumAmount}
      change={
        offThatchOrMedicare
          ? null
          : planChanges.year_over_year_change.premium_total
      }
      xAxisMaxAmountSubunits={maxAmountSubunits}
      increaseIsGreen={false}
      useMinLabelWidth={isQle}
    >
      {showCoveredMessage && (
        <p className='flex items-center py-1 pl-2 pr-3 rounded-xl bg-green-50 mt-4 w-fit'>
          <CheckIcon className='h-5 w-5 text-green-400 mr-1' />
          <span className='body-sm text-gray-800'>
            You won't need to pay anything since your allowance covers your
            insurance cost
          </span>
        </p>
      )}
      {offThatchOrMedicare && (
        <p className='flex items-start py-1 pl-2 pr-3 rounded-xl bg-slate-50 mt-4 w-fit'>
          <InfoIcon size={16} className='h-5 w-5 text-slate-400 mr-1 shrink-0' />
          <span className='body-sm text-slate-500'>
            Since you purchased at least one plan outside of Thatch, we can't
            determine the cost
          </span>
        </p>
      )}
    </BudgetComparisonSection>
  )
}

const getRemainderSectionContent = ({
  directionDifferent,
  remainingBalanceIsPositive,
  remainingBalance,
  yoyChange,
}: {
  directionDifferent: boolean;
  remainingBalanceIsPositive: boolean;
  remainingBalance: Money;
  yoyChange: Money;
}) => {
  let title = ''
  let condensedTitle = ''
  let description = null

  if (remainingBalanceIsPositive) {
    title = 'Remainder added to your balance each month'
    condensedTitle = 'Remainder added to your balance'
  } else {
    title = 'Your monthly contribution'
    condensedTitle = 'Your monthly contribution'
  }

  if (remainingBalanceIsPositive) {
    if (yoyChange.subunits >= 0) {
      description = (
        <>
          You can spend your leftover balance on healthcare expenses like
          premiums, co-pays, or exclusive deals in the{' '}
          <TextLink href={membersMarketplace.index()} target='_blank'>
            Thatch marketplace
          </TextLink>
          .
        </>
      )
    } else {
      description = (
        <>
          Although insurance rates are rising next year, you'll still be saving{' '}
          {remainingBalance.formatted_amount.replace('-', '')} each month to
          spend on healthcare expenses like premiums, co-pays, or exclusive
          deals in the{' '}
          <TextLink href={membersMarketplace.index()} target='_blank'>
            Thatch marketplace
          </TextLink>
          .
        </>
      )
    }
  } else {
    if (remainingBalance.subunits === 0) {
      description =
        "Due to higher insurance rates this year, you won't accumulate any leftover funds from your allowance."
    } else if (directionDifferent) {
      description = `Due to higher rates, you'll have ${remainingBalance.formatted_amount.replace(
        '-',
        ''
      )} deducted from your paychecks every month to cover the portion of your insurance costs not covered by your monthly allowance.`
    } else {
      description = `You'll have ${remainingBalance.formatted_amount.replace(
        '-',
        ''
      )} deducted from your paychecks every month to cover the portion of your insurance costs not covered by your monthly allowance.`
    }
  }
  return { title, condensedTitle, description }
}

const getRemainderSectionContentQLE = ({
  remainingBalanceIsPositive,
  remainingBalance,
  qmeEnabled,
  planChanges,
}: {
  remainingBalanceIsPositive: boolean;
  remainingBalance: Money;
  qmeEnabled: boolean;
  planChanges: MemberOnboardingMemberPlanChangesProps;
}) => {
  let title = ''
  let condensedTitle = ''
  let description = null

  if (remainingBalanceIsPositive) {
    title = 'Remainder added to your balance each month'
    condensedTitle = 'Remainder added to your balance'
  } else {
    title = 'Your monthly contribution'
    condensedTitle = 'Your monthly contribution'
  }

  if (remainingBalanceIsPositive) {
    // In the context of this page, before and after plans should be the same since the user hasn't change their plan elections yet
    // Determine if there are multiple plans (dental and/or vision in addition to medical)
    const hasMultiplePlans = !!(
      planChanges.current_plan_year.dental_plan || planChanges.current_plan_year.vision_plan
    )

    if (qmeEnabled) {
      description = (
        <>
          You'll get {remainingBalance.formatted_amount.replace('-', '')} added to your Thatch balance every month to spend on things like copays, prescriptions, and the{' '}
          <TextLink href={membersMarketplace.index()} target='_blank'>
            Thatch Marketplace
          </TextLink>
          .
        </>
      )
    } else {
      description = (
        <>
          You won't pay anything out of pocket for your insurance if you keep your current plan{hasMultiplePlans ? 's' : ''}.
        </>
      )
    }
  } else {
    if (remainingBalance.subunits === 0) {
      description = "You won't accumulate any leftover funds from your allowance."
    } else {
      description = `You'll have ${remainingBalance.formatted_amount.replace(
        '-',
        ''
      )} deducted from your paychecks every month to cover the portion of your insurance costs not covered by your monthly allowance.`
    }
  }
  return { title, condensedTitle, description }
}

export const RemainderOrYouPaySection = ({
  condensed,
  planChanges,
  maxAmountSubunits,
  qmeEnabled,
  directionDifferent,
  remainingBalance,
  remainingBalanceIsPositive,
  enrollmentEvent,
}: RemainderOrYouPaySectionProps) => {
  const yoyChange = planChanges.year_over_year_change.remaining_balance
  const offThatchOrMedicare =
    isCurrentPlanOffThatch(planChanges) && isNextPlanEmpty(planChanges)

  const isQle = isEnrollmentEventQle(enrollmentEvent)
  const { title, condensedTitle, description } = isQle
    ? getRemainderSectionContentQLE({
      remainingBalanceIsPositive,
      remainingBalance,
      qmeEnabled,
      planChanges,
    })
    : getRemainderSectionContent({
      directionDifferent,
      remainingBalanceIsPositive,
      remainingBalance,
      yoyChange,
    })
  const beforeLabel = isQle ? 'Before' : planChanges.current_plan_year.plan_year.toString()
  const afterLabel = isQle ? 'After' : planChanges.next_plan_year.plan_year.toString()

  return (
    <BudgetComparisonSection
      title={condensed ? condensedTitle : title}
      description={description}
      condensed={condensed}
      beforeLabel={beforeLabel}
      afterLabel={afterLabel}
      beforeAmount={planChanges.current_plan_year.remaining_balance}
      afterAmount={
        offThatchOrMedicare
          ? null
          : planChanges.next_plan_year.remaining_balance
      }
      change={
        offThatchOrMedicare
          ? null
          : planChanges.year_over_year_change.remaining_balance
      }
      xAxisMaxAmountSubunits={maxAmountSubunits}
      increaseIsGreen={remainingBalanceIsPositive}
      useMinLabelWidth={isQle}
    >
      {remainingBalanceIsPositive && qmeEnabled && (
        <div className='mt-4'>
          <MarketplaceProductSection
            filterToCategories={[
              'Fitness',
              'Preventative care',
              'Sleep',
              'Mental health',
              'Telehealth',
            ]}
          />
        </div>
      )}
    </BudgetComparisonSection>
  )
}

const getMaxAmountAmongAllPlanChanges = (
  planChanges: MemberOnboardingMemberPlanChangesProps,
  directionDifferent: boolean
) => {
  if (directionDifferent) {
    const buffer = Math.abs(
      planChanges.current_plan_year.remaining_balance.subunits
    )
    return (
      Math.max(
        planChanges.current_plan_year.allowance_amount.subunits,
        planChanges.next_plan_year.allowance_amount.subunits,
        planChanges.current_plan_year.premium_total.subunits,
        planChanges.next_plan_year.premium_total.subunits,
        Math.abs(planChanges.next_plan_year.remaining_balance.subunits)
      ) + buffer
    )
  }

  return Math.max(
    planChanges.current_plan_year.allowance_amount.subunits,
    planChanges.next_plan_year.allowance_amount.subunits,
    planChanges.current_plan_year.premium_total.subunits,
    planChanges.next_plan_year.premium_total.subunits,
    Math.abs(planChanges.current_plan_year.remaining_balance.subunits),
    Math.abs(planChanges.next_plan_year.remaining_balance.subunits)
  )
}

const BudgetAndPlanChangesSection: React.FC<
  BudgetAndPlanChangesSectionProps
> = ({ condensed, planChanges, qmeEnabled, hasTieredAllowances, companyName, enrollmentEvent }) => {
  const remainingBalance = planChanges.next_plan_year.remaining_balance
  const remainingBalanceIsPositive = remainingBalance.subunits > 0
  const directionDifferent =
    planChanges.current_plan_year.remaining_balance.subunits *
      remainingBalance.subunits <
    0
  const maxAmountSubunits = getMaxAmountAmongAllPlanChanges(
    planChanges,
    directionDifferent
  )
  const shouldNotRenderRemainderSection =
   (!qmeEnabled && remainingBalanceIsPositive) || hasTieredAllowances

  return (
    <div
      data-testid='budget-and-plan-changes-section'
      className={cn(
        'w-full border border-gray-200 rounded-2xl shadow-sm transition-all duration-700 overflow-hidden grid',
        {
          'gap-y-10 p-6 grid-cols-1': !condensed,
          'grid-rows-[auto_auto]':
              !condensed && shouldNotRenderRemainderSection,
          'grid-rows-[auto_auto_auto]':
              !condensed && !shouldNotRenderRemainderSection,
          'gap-x-4 p-4 grid-rows-1 grid-cols-3 bg-white shadow-lg': condensed,
        }
      )}
    >
      <MonthlyAllowanceSection
        condensed={condensed}
        companyName={companyName}
        planChanges={planChanges}
        maxAmountSubunits={maxAmountSubunits}
        enrollmentEvent={enrollmentEvent}
      />
      <MonthlyPremiumSection
        condensed={condensed}
        planChanges={planChanges}
        maxAmountSubunits={maxAmountSubunits}
        enrollmentEvent={enrollmentEvent}
      />
      {!shouldNotRenderRemainderSection
        ? (
          <RemainderOrYouPaySection
            condensed={condensed}
            remainingBalance={remainingBalance}
            remainingBalanceIsPositive={remainingBalanceIsPositive}
            planChanges={planChanges}
            maxAmountSubunits={maxAmountSubunits}
            qmeEnabled={qmeEnabled}
            directionDifferent={directionDifferent}
            enrollmentEvent={enrollmentEvent}
          />
          )
        : condensed
          ? (
            <div />
            )
          : null}
    </div>
  )
}

export default BudgetAndPlanChangesSection
